import { z } from "zod";

/**
 * Fonte unica de verdade dos tipos de auth (CLAUDE.md 8): os tipos saem do
 * schema por `z.infer`, nunca declarados em paralelo — interface escrita a
 * mao ao lado de um schema Zod sai de sincronia na primeira mudanca.
 */

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Informe seu e-mail")
    .email("Digite um e-mail válido")
    .transform((value) => value.trim().toLowerCase()),
  password: z.string().min(1, "Informe sua senha"),
});

export type LoginInput = z.infer<typeof loginSchema>;

export const notificationPrefsSchema = z.object({
  email_new_message: z.boolean(),
  email_new_orientation: z.boolean(),
  email_alert: z.boolean(),
  email_reminder: z.boolean(),
});

export const sessionUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.enum(["therapist", "guardian"]),
  status: z.enum(["invited", "active", "suspended"]),
  avatar_url: z.string().nullable(),
  consent_given: z.boolean().nullable(),
  signature: z.string().nullable(),
  notification_prefs: notificationPrefsSchema,
  created_at: z.string().nullable(),
});

export const sessionOperatorSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  status: z.string(),
  role: z.literal("operator"),
});

export const sessionTenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  status: z.string(),
  plan: z.string(),
  timezone: z.string(),
});

export const sessionSchema = z.object({
  user: sessionUserSchema.optional(),
  operator: sessionOperatorSchema.optional(),
  tenant: sessionTenantSchema.optional(),
  expires_at: z.string().optional(),
});

export type SessionResponse = z.infer<typeof sessionSchema>;

/** RF-02.5: pedido de reset. Resposta do backend e sempre 202 neutro
 *  (RNF-02.4) — o formulario nao tem como saber, e nao deve tentar saber,
 *  se o e-mail existe. */
export const requestPasswordResetSchema = z.object({
  email: z
    .string()
    .min(1, "Informe seu e-mail")
    .email("Digite um e-mail válido")
    .transform((value) => value.trim().toLowerCase()),
});
export type RequestPasswordResetInput = z.infer<typeof requestPasswordResetSchema>;

/** "Esqueci minha clinica": mesmo formato do reset de senha — o backend
 *  tambem responde sempre 202 neutro aqui (RNF-02.4). */
export const findTenantSchema = z.object({
  email: z
    .string()
    .min(1, "Informe seu e-mail")
    .email("Digite um e-mail válido")
    .transform((value) => value.trim().toLowerCase()),
});
export type FindTenantInput = z.infer<typeof findTenantSchema>;

/** RF-02.4/02.5: define senha — serve tanto para ativar um convite quanto
 *  para redefinir uma senha esquecida (o backend usa o mesmo endpoint). */
export const setPasswordSchema = z
  .object({
    password: z.string().min(10, "A senha precisa ter pelo menos 10 caracteres").max(72),
    confirmPassword: z.string().min(1, "Confirme a senha"),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: "As senhas não coincidem",
    path: [ "confirmPassword" ],
  });
export type SetPasswordInput = z.infer<typeof setPasswordSchema>;
